<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import dbaInfographicMobile from './images/db-infographic-mobile.png';
	import dbaInfographic from './images/dba-infographic.png';
	import ratingLimitation from './images/rating-limitation.jpeg';
	import stcRatingChartForWindows from './images/stc-rating-chart-for-windows.jpeg';
	import youtubeThumbnail from './images/y_5YPwKU5HM.jpg?w=610';
</script>

<Section>
	<p>
		STC rating (Sound Transmission Class rating) is a score given to a
		building’s surface (wall, ceiling, window, etc.) based on its ability to
		reduce sound coming through it.
	</p>
	<p>
		STC rating goes from 25 – 65. The higher the rating, the less noise
		penetration. A rating of 25 is no soundproofing, and 65 is complete
		soundproofing. A standard single-pane window would have an STC rating of 25.
		Most “soundproof” products have an STC rating in the range of 35 – 55.
		Industry average for STC-rated windows is 38.
	</p>
</Section>
<Section>
	<h2>Video: STC Ratings of Materials</h2>
	<Youtube
		src="https://www.youtube.com/embed/y_5YPwKU5HM"
		title="Video: STC Ratings of Materials"
		posterImageSrc={youtubeThumbnail}
	/>
	<p>
		STC rating is a way to compare products for their ability to stop the
		transition of sound. Using this number you can compare various products
		using the same rating system. You can compare window to window, but also
		window to door for their ability to stop outside noise from coming into your
		space. This will help you understand which surfaces are allowing the most
		sound into your home.
	</p>
</Section>
<Section backgroundColor="white">
	<h2>STC Rating Chart</h2>
	<p>
		This STC rating chart shows a variety of building materials, their average
		STC rating and what noise can and cannot be heard at that rating. Specific
		products for these building materials will vary, so talk to the manufacturer
		to get the exact STC rating before purchasing.
	</p>
	<table class="w-full text-center text-base text-black">
		<thead class="bg-indow-blue">
			<tr class="[&_th]:border [&_th]:px-4 [&_th]:py-2 [&_th]:font-medium">
				<th>BUILDING SURFACE</th>
				<th>STC RATING</th>
				<th>SOUNDS LIKE</th>
			</tr>
		</thead>
		<tbody class="[&_td]:border [&_td]:p-4">
			<tr>
				<td class="text-indow-blue">Single-Pane Window</td>
				<td>25</td>
				<td class="text-indow-blue">Normal speech is clear</td>
			</tr>
			<tr>
				<td class="text-indow-blue">Double-Pane Window</td>
				<td>33-35</td>
				<td class="text-indow-blue">Loud speech is clear</td>
			</tr>
			<tr>
				<td class="text-indow-blue"
					>Indow Insert & Single-Pane Window<span class="text-black">*</span
					></td
				>
				<td>39</td>
				<td class="text-indow-blue">Loud speech sounds like a hum</td>
			</tr>
			<tr>
				<td class="text-indow-blue"
					>Indow Insert & Double-Pane Window<span class="text-black">**</span
					></td
				>
				<td>42-45</td>
				<td class="text-indow-blue"
					>Loud speech/music mostly blocked except for bass</td
				>
			</tr>
			<tr>
				<td class="text-indow-blue">8" slab</td>
				<td>45</td>
				<td class="text-indow-blue">Loud speech cannot be heard</td>
			</tr>
			<tr>
				<td class="text-indow-blue">10" Masonry Wall</td>
				<td>50</td>
				<td class="text-indow-blue">Loud music barely heard</td>
			</tr>
			<tr>
				<td></td>
				<td>65+</td>
				<td class="text-indow-blue">"Soundproof"</td>
			</tr>
		</tbody>
	</table>
	<div class="flex gap-6">
		<span class="text-indow-blue!">
			<span class="text-black">*</span> Acoustic Grade insert with 3" gap
		</span>
		<span class="text-indow-blue!">
			<span class="text-black">**</span> Acoustic Grade insert
		</span>
	</div>
	<p>
		Things to note: STC rating of 25 is what can be easily heard. STC rating of
		38-42 is considered good soundproofing for residential. STC rating of 50-65
		is professional, commercial soundproofing for recording studios, etc.
	</p>
</Section>
<Section backgroundColor="blue">
	<h2>Other Sound Transmission Definitions</h2>
	<p>
		These terms and definitions are related to Sound Transmission Class and will
		help provide the full picture of how sound is measured.
	</p>
	<p>
		dBa – A-weighted decibels. Measurement of sound as perceived by the human
		ear. Decibels relate to all sound possible (that can be perceived by say, a
		dog). A-weighted decibels are a scale of measurement for all sound that can
		be heard by humans, starting with zero as the lowest threshold of human
		hearing and going up to 110 dBA for jet fly-over/indoor rock band.
	</p>

	<Image
		src={dbaInfographic}
		alt="STC noise reduction soundproofing infographic"
		class="
    no-bleed hidden
    md:block
  "
	/>
	<Image
		src={dbaInfographicMobile}
		alt="infographic of dBa levels and examples of relatives noises"
		class="
    px-4
    md:hidden
  "
	/>
	<p>
		<strong>Transmission Loss –</strong> How much sound is lost going through a material.
		When sound reaches an object, some of the noise vibrations are reflected by it,
		some are absorbed, and some travel through it. The amount of sound that does not
		travel through it, is considered loss. It is the difference between the sound
		going into the object and the sound coming out the other side.
	</p>
	<p>
		<strong>Frequency (Hz) –</strong> Sound is vibrations that travel through the
		air. Frequency is the speed of the vibrations. Frequency is measured in units
		of Hertz (Hz). Hertz measure the number of wave cycles per second. Low frequency
		sounds, like thunder or a train, would have a lower Hz. High frequency sounds,
		like a whistle or a bird chirping, would have a higher Hz.
	</p>
	<p>
		For more definitions see our <a
			href="/resources/faq/interior-storm-window-inserts-glossary"
			>glossary of acoustic terms.</a
		>
	</p>
	<p>
		While STC rating can provide a good indication of how a material or product
		will perform against sound in general, it isn’t the whole picture. That’s
		why it’s good to keep these other definitions in mind. Certain sounds or
		noises will affect different people differently. STC rating is one number
		that shows general sound resistance, but does not take Hz into account.
	</p>
</Section>
<Section>
	<h2>STC Rating Limitations</h2>
	<p>
		STC is a single number used to describe the ability of an assembly (how a
		product is put together) to reduce noise against a wide range of
		frequencies. An STC rating alone won’t show what Hz will be blocked. STC
		rating only gives you an overall average of noise blocking.
	</p>
	<p>
		Different products work differently to block different noise so you must
		compare exact product assemblies to each other (e.g.: a double-pane window
		with the same air gap). Talk to the manufacturer about what their product
		can block if you are having issues with a particular noise (e.g.: dog
		barking, a train horn).
	</p>
	<Image
		src={ratingLimitation}
		alt="woman installing Indow insert for higher stc rated windows"
		class="no-bleed"
	/>
	<h1>STC Rating Windows</h1>
	<p>
		STC rated windows range from 25-35. This is for regular windows without the
		addition of <a href="/resources/blog/soundproof-windows-home-depot"
			>soundproofing materials</a
		>. The STC rating of windows can be affected by: whether they are single or
		double-pane, the size of the gap between the panes, if gas is inserted
		between the panes, the thickness of the glass, and how airtight they are.
	</p>
	<p>
		STC rated windows can increase to a 39-45 rating with the addition of window
		inserts such as <a href="/products/acoustic-window-inserts"
			>Indow Acoustic Grade</a
		>. Indow inserts are made of acrylic, which absorbs and resists sound better
		than glass. Adding another layer to your window via a window insert also
		creates an air pocket that insulates against sound. STC rating windows of
		42-45 usually only happens when Indow window inserts are added to
		double-pane windows.
	</p>
	<h1>STC Rating Chart For Windows</h1>
	<p>
		This STC rating chart for windows goes down from lowest to highest STC rated
		windows to represent the quieter environment you will experience.
	</p>
	<Image
		src={stcRatingChartForWindows}
		alt="stc rating chart of stc rated windows"
		class="no-bleed"
	/>
	<h1>Is Complete Soundproofing Possible?</h1>
	<p>
		Our STC rating chart above only goes to 50 because STC rated windows are in
		that range. The STC rating goes all the way up to 65. Most products don’t
		promise anything above 55 and even at a rating of 60, it still doesn’t
		guarantee complete soundproofing. Nothing is completely soundproof. You
		cannot stop outside noise completely from entering your space. Anywhere air
		can get in, noise can get in.
	</p>
	<p class="italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
</Section>
<GetAFreeEstimate />
